// src/services/ecrfExtractionService.js
//
// SourceX Roadmap Phase 1b — client data layer for blank-eCRF ingestion.
//
// Mirrors protocolExtractionService.js:
//   - upload an eCRF PDF to Storage + create the eCRF Firestore doc
//   - kick off / resume server-side extraction (authenticated Cloud Function)
//   - read the merged extraction for the review UI
//
// All the expensive work (segmentation, chunking, model calls, merge, branch
// resolution) runs server-side in extractEcrfForms. This file only orchestrates
// and reads/writes Firestore.
//
// RESUME: a 400-900 page eCRF can exceed the 540s function limit. The server
// stops cleanly at a time budget and returns `nextChunk`; `runExtraction` below
// loops until complete. Completed chunks are persisted server-side and skipped
// on resume, so a resumed run costs NO extra tokens.

import { db, storage, auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getActiveTenantId, getActiveSiteId } from "./activeTenant";
import {
  getStudyEcrfsPath,
  getStudyEcrfDocPath,
  getEcrfExtractionDocPath,
} from "./firestorePaths";
import { ECRF_STATUS, ECRF_EXTRACTION_VERSION } from "./ecrfTypes";

const FN_BASE = FUNCTIONS_BASE;

const lc = (s) => String(s || "").toLowerCase();
const sanitize = (name) => String(name || "file.pdf").replace(/[^\w.-]/g, "_");

const callFn = async (name, body) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();
  const resp = await fetch(`${FN_BASE}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body || {}),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `${name} failed (${resp.status}).`);
  return data;
};

/**
 * Upload a blank eCRF PDF and create its Firestore doc (status "uploaded").
 * Storage is namespaced by tenant/site/study/ecrf, mirroring the Firestore
 * isolation.
 * @returns {Promise<{ecrfId:string, storagePath:string, downloadUrl:string}>}
 */
export const uploadAndCreateEcrf = async (studyId, file) => {
  if (!studyId) throw new Error("studyId is required.");
  if (!file) throw new Error("No file selected.");

  const tenantId = getActiveTenantId();
  const siteId = getActiveSiteId();

  // Reserve the id first so the Storage path is stable and traceable.
  const ecrfRef = doc(collection(db, getStudyEcrfsPath(studyId)));
  const ecrfId = ecrfRef.id;

  const storagePath = `ecrfs/${tenantId}/${siteId}/${studyId}/${ecrfId}/original_${sanitize(
    file.name
  )}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, {
    contentType: file.type || "application/pdf",
  });
  const downloadUrl = await getDownloadURL(storageRef);

  await setDoc(ecrfRef, {
    fileName: file.name || "",
    storagePath,
    downloadUrl,
    studyId,
    tenantId,
    siteId,
    status: ECRF_STATUS.UPLOADED,
    extractionVersion: ECRF_EXTRACTION_VERSION,
    pageCount: 0,
    formCount: 0,
    fieldCount: 0,
    errorMessage: "",
    createdBy: lc(auth.currentUser?.email),
    uploadedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
  });

  return { ecrfId, storagePath, downloadUrl };
};

/**
 * One server pass. Prefer runExtraction() unless you're driving resume yourself.
 * @param {"batch"} [mode] queue the read asynchronously (~half price) instead of
 *        running it now. Ignored by the server unless batch is enabled there.
 */
export const startExtraction = async (studyId, ecrfId, startChunk = 0, mode) =>
  callFn("extractEcrfForms", {
    studyId,
    ecrfId,
    startChunk,
    ...(mode ? { mode } : {}),
    tenantId: getActiveTenantId(),
    siteId: getActiveSiteId(),
  });

/**
 * Check a queued batch and collect it if the results are ready.
 *
 * Deliberately ONE pass, not a loop: a batch can take hours, so the browser
 * must never sit on it. Call this on page load and on demand; the eCRF document
 * is live via listenEcrfs, so the page updates itself the moment a call
 * collects. Returns the same shape as startExtraction.
 */
export const collectBatch = async (studyId, ecrfId) =>
  startExtraction(studyId, ecrfId, 0);

/**
 * Run extraction to completion, resuming across function invocations when a
 * large eCRF exceeds the 540s limit.
 *
 * @param {string} studyId
 * @param {string} ecrfId
 * @param {(p:{pass:number, chunksComplete:number, chunkCount:number, complete:boolean, usage:object}) => void} [onProgress]
 * @param {{maxPasses?:number}} [opts]
 */
export const runExtraction = async (studyId, ecrfId, onProgress, opts = {}) => {
  // A ceiling on resume passes, so a server that stops making progress can
  // never turn into an unbounded billing loop.
  const maxPasses = opts.maxPasses ?? 12;
  let startChunk = 0;

  for (let pass = 1; pass <= maxPasses; pass += 1) {
    // `mode` is only meaningful on the FIRST pass — it submits the batch. Later
    // passes collect it, and passing the mode again would queue a second batch.
    const result = await startExtraction(
      studyId,
      ecrfId,
      startChunk,
      pass === 1 ? opts.mode : undefined
    );
    onProgress?.({
      pass,
      chunksComplete: result.chunksComplete || 0,
      chunkCount: result.chunkCount || 0,
      complete: !!result.complete,
      batchPending: !!result.batchPending,
      usage: result.usage || null,
    });

    if (result.complete) return result;

    // A queued batch is a SUCCESSFUL outcome, not a stall: the work is with the
    // provider and results land in minutes to hours. Return so the browser
    // stops waiting — the eCRF doc is live, and the next visit collects it.
    if (result.batchPending) return result;

    // No forward progress means resuming again would just repeat the failure
    // and bill for it. Stop and surface it instead.
    if (
      typeof result.nextChunk !== "number" ||
      (startChunk > 0 && result.nextChunk <= startChunk && !result.chunksThisRun)
    ) {
      throw new Error(
        "Extraction stopped without making progress. Check the eCRF status for details."
      );
    }
    startChunk = result.nextChunk;
  }

  throw new Error(
    `Extraction did not finish after ${maxPasses} passes. The eCRF may be unusually large — its completed chunks are saved, so resuming will continue where it stopped.`
  );
};

/** Live list of a study's uploaded eCRFs, newest first. Returns an unsubscribe. */
export const listenEcrfs = (studyId, onData, onError) => {
  const q = query(
    collection(db, getStudyEcrfsPath(studyId)),
    orderBy("uploadedAt", "desc")
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error("listenEcrfs error:", err);
      onError && onError(err);
    }
  );
};

export const getEcrf = async (studyId, ecrfId) => {
  const snap = await getDoc(doc(db, getStudyEcrfDocPath(studyId, ecrfId)));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** The merged, branch-resolved extraction, or null if not yet produced. */
export const getEcrfExtraction = async (studyId, ecrfId) => {
  const snap = await getDoc(doc(db, getEcrfExtractionDocPath(studyId, ecrfId)));
  return snap.exists() ? snap.data() : null;
};

export const listenEcrfExtraction = (studyId, ecrfId, onData, onError) =>
  onSnapshot(
    doc(db, getEcrfExtractionDocPath(studyId, ecrfId)),
    (snap) => onData(snap.exists() ? snap.data() : null),
    (err) => {
      console.error("listenEcrfExtraction error:", err);
      onError && onError(err);
    }
  );
